import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Pause, Play, RotateCcw, Pencil, Timer } from "lucide-react";
import { CHANNEL } from "./sync";
import { useNotes } from "./useNotes";
import { NoteEditor } from "./NoteEditor";
import { useTTS } from "./useTTS";
import { TtsControls, TtsStatus } from "./TtsControls";
import { stripMarks } from "./prosody";

const Emph = ({ text }) =>
  text.split(/(\*[^*]+\*)/).map((p, i) =>
    /^\*[^*]+\*$/.test(p) ? <strong key={i} className="font-semibold text-dash">{p.slice(1, -1)}</strong> : <span key={i}>{p}</span>
  );
import { SLIDE_META, SLIDE_STARTS, TOTAL_SECONDS, EASE } from "./config";
import { DashMark } from "./ui";

const fmt = (s) => {
  const v = Math.max(0, Math.ceil(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

const Ctrl = ({ onClick, label, testId, children, primary }) => (
  <button
    onClick={onClick}
    aria-label={label}
    data-testid={testId}
    className={`grid h-12 w-12 place-items-center rounded-full border transition-colors duration-300 ${
      primary ? "border-[var(--dash)] bg-[var(--dash)] text-black hover:bg-[#ff8a33]" : "border-white/15 text-zinc-100 hover:border-white/40"
    }`}
  >
    {children}
  </button>
);

const SlideClock = ({ index, elapsed }) => {
  const start = SLIDE_STARTS[index];
  const budget = SLIDE_META[index].budget;
  const left = start + budget - elapsed;
  const pct = Math.min(100, Math.max(0, ((elapsed - start) / budget) * 100));
  const over = left < 0;
  return (
    <div data-testid="presenter-slide-clock">
      <div className="flex items-baseline justify-between">
        <p className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-zinc-400">This slide · {budget}s</p>
        <p className={`font-mono-d text-2xl tabular-nums ${over ? "text-red-300" : "text-zinc-50"}`}>
          {over ? `+${fmt(-left)}` : fmt(left)}
        </p>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div className={`h-full ${over ? "bg-red-400" : "bg-[var(--dash)]"}`} style={{ width: `${over ? 100 : pct}%`, transition: "width 120ms linear" }} />
      </div>
    </div>
  );
};

export default function PresenterView() {
  const [state, setState] = useState(null);
  const [editing, setEditing] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("dash-tts-lang") || "en-US");
  const { notes, save, resetOne } = useNotes(lang);
  const chRef = useRef(null);
  const prevElapsed = useRef(0);
  const curIndex = state?.index ?? 0;
  const tts = useTTS({
    index: curIndex,
    running: !!state?.running,
    connected: !!state,
    lang,
    getLines: () => notes[curIndex]?.lines ?? [],
    onFinished: () => {
      if (curIndex < SLIDE_META.length - 1) chRef.current?.postMessage({ type: "next" });
    },
  });
  const { stop: ttsStop, restart: ttsRestart, enabled: ttsOn, setEnabled: setTtsOn } = tts;
  const startRef = useRef(null);
  startRef.current = () => {
    localStorage.setItem("dash-tts-lang", "en-US");
    setLang("en-US");
    setTtsOn(true);
  };

  const changeLang = (code) => {
    localStorage.setItem("dash-tts-lang", code);
    setLang(code);
  };

  useEffect(() => {
    const e = state?.elapsed ?? 0;
    if (e < prevElapsed.current - 1 && ttsOn) {
      ttsStop();
      chRef.current?.postMessage({ type: "first" });
      ttsRestart();
    }
    prevElapsed.current = e;
  }, [state?.elapsed, ttsOn, ttsStop, ttsRestart]);
  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL);
    chRef.current = ch;
    ch.onmessage = (e) => {
      if (e.data?.type === "state") setState(e.data);
      else if (e.data?.type === "show-start") startRef.current?.();
    };
    ch.postMessage({ type: "hello" });
    return () => ch.close();
  }, []);

  const send = (msg) => chRef.current?.postMessage(msg);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (["ArrowRight", "ArrowDown", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        chRef.current?.postMessage({ type: "next" });
      } else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        chRef.current?.postMessage({ type: "prev" });
      } else if (e.key === "p") chRef.current?.postMessage({ type: "toggle" });
      else if (e.key === "r") chRef.current?.postMessage({ type: "reset" });
      else if (e.key === "a") chRef.current?.postMessage({ type: "rehearse" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!state) {
    return (
      <div className="deck-root grid place-items-center p-8 text-center" data-testid="presenter-waiting">
        <div>
          <DashMark className="mx-auto h-10 w-auto" />
          <p className="mt-6 font-display text-2xl font-semibold text-zinc-50">Waiting for the deck…</p>
          <p className="mt-2 text-sm text-zinc-400">Keep the main deck open in another tab or window of this browser.</p>
        </div>
      </div>
    );
  }

  const { index, elapsed, running, rehearse } = state;
  const note = notes[index];
  const next = SLIDE_META[index + 1];

  return (
    <div className="deck-root flex flex-col overflow-y-auto" data-testid="presenter-view">
      <div className="relative h-[3px] w-full shrink-0 bg-white/[0.07]">
        <div className="absolute inset-y-0 left-0 bg-[var(--dash)]" style={{ width: `${(elapsed / TOTAL_SECONDS) * 100}%`, transition: "width 120ms linear" }} />
      </div>
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 px-6 py-4 lg:px-10">
        <div className="flex items-center gap-3">
          <DashMark className="h-7 w-auto" />
          <p className="font-mono-d text-[11px] uppercase tracking-[0.22em] text-zinc-400">Presenter view</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <TtsControls tts={tts} lang={lang} onLang={changeLang} locked={editing !== null} />
          <button
            onClick={() => send({ type: "rehearse" })}
            data-testid="presenter-rehearsal-toggle"
            className={`flex h-12 items-center gap-2 rounded-full border px-4 text-sm transition-colors duration-300 ${
              rehearse ? "border-dash bg-dash text-black" : "border-white/15 text-zinc-100 hover:border-dash"
            }`}
          >
            <Timer size={17} />
            <span className="hidden sm:inline">{rehearse ? "Stop rehearsal" : "Rehearse"}</span>
          </button>
          <span className="font-mono-d text-3xl tabular-nums text-zinc-50" data-testid="presenter-total-remaining">{fmt(TOTAL_SECONDS - elapsed)}</span>
          <Ctrl onClick={() => send({ type: "toggle" })} label={running ? "Pause timer" : "Start timer"} testId="presenter-play-pause">
            {running ? <Pause size={18} /> : <Play size={18} />}
          </Ctrl>
          <Ctrl onClick={() => send({ type: "reset" })} label="Reset timer" testId="presenter-reset">
            <RotateCcw size={18} />
          </Ctrl>
        </div>
      </header>

      <main className="grid flex-1 gap-8 px-6 py-8 lg:grid-cols-12 lg:px-10">
        <section className="lg:col-span-8">
          <div className="flex items-center justify-between gap-3">
            <p className="font-mono-d text-[11px] uppercase tracking-[0.22em] text-zinc-400" data-testid="presenter-current-slide">
              <span className="text-dash">{String(index + 1).padStart(2, "0")}</span> / 08 · {SLIDE_META[index].label}
              {rehearse && <span className="ml-3 text-orange-200">· Rehearsal auto-advance</span>}
            </p>
            {editing === null && (
              <button onClick={() => setEditing(index)} data-testid="presenter-edit-notes-button" className="flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-xs text-zinc-200 transition-colors hover:border-dash hover:text-dash">
                <Pencil size={13} /> Edit script
              </button>
            )}
          </div>
          <TtsStatus tts={tts} />
          {editing !== null ? (
            <>
            {editing !== index && <p className="mt-3 text-xs text-orange-200">Editing slide {String(editing + 1).padStart(2, "0")} · {SLIDE_META[editing].label}</p>}
            <p className="mt-3 font-mono-d text-[11px] uppercase tracking-[0.2em] text-orange-200" data-testid="presenter-editing-lang">Editing {lang === "id-ID" ? "Indonesia" : "English"} script</p>
            <NoteEditor
              key={`${editing}-${lang}`}
              note={notes[editing]}
              onSave={(n) => { save(editing, n); setEditing(null); }}
              onCancel={() => setEditing(null)}
              onReset={() => { resetOne(editing); setEditing(null); }}
            />
            </>
          ) : (
          <>
          <p className="mt-3 rounded-xl border border-dash/30 bg-dash/10 px-4 py-2.5 text-sm text-orange-200" data-testid="presenter-cue">
            {note.cue}
          </p>
          <AnimatePresence initial={false}>
            <motion.ul
              key={index}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="mt-6 space-y-4"
              data-testid="presenter-notes"
            >
              {note.lines.map((l, i) => (
                <li
                  key={l}
                  data-testid={`presenter-note-line-${i + 1}`}
                  className={`border-l-2 pl-4 font-display text-2xl leading-snug transition-colors duration-300 lg:text-[1.9rem] ${
                    tts.enabled && tts.line === i ? "border-dash text-zinc-50" : "border-transparent text-zinc-200"
                  }`}
                >
                  <Emph text={l} />
                </li>
              ))}
            </motion.ul>
          </AnimatePresence>
          </>
          )}
        </section>

        <aside className="flex flex-col gap-6 lg:col-span-4">
          <div className="rounded-3xl border border-white/10 bg-[var(--panel)] p-5">
            <SlideClock index={index} elapsed={elapsed} />
          </div>
          <div className="rounded-3xl border border-white/10 bg-[var(--panel)] p-5" data-testid="presenter-next-slide">
            <p className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-zinc-400">Up next</p>
            {next ? (
              <>
                <p className="mt-2 font-display text-xl font-semibold text-zinc-50">{String(index + 2).padStart(2, "0")} · {next.label}</p>
                <p className="mt-2 text-sm leading-relaxed text-zinc-300">{stripMarks(notes[index + 1].lines[0])}</p>
              </>
            ) : (
              <p className="mt-2 font-display text-xl font-semibold text-zinc-50">End of deck</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Ctrl onClick={() => send({ type: "prev" })} label="Previous slide" testId="presenter-prev">
              <ArrowLeft size={18} />
            </Ctrl>
            <Ctrl onClick={() => send({ type: "next" })} label="Next slide" testId="presenter-next" primary>
              <ArrowRight size={18} />
            </Ctrl>
            <span className="font-mono-d text-[11px] uppercase tracking-[0.18em] text-zinc-500">← → · p · r · a</span>
          </div>
        </aside>
      </main>
    </div>
  );
}
